/* eslint-disable no-nested-ternary */
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import {
  Button,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Typography,
  Tag,
} from "antd";
import moment from "moment";

import useDebounce from "../../hooks/useDebounce";
import {
  approveManagePlan,
  getManagePlanRequests,
  getManagePlanRequestedUsers,
  rejectManagePlan,
} from "../api";

import ManagePlanModal from "../../Components/ManagePlanModal";
import {
  TIER_NAME_MAP,
  EXPIRY_INTERVAL,
  DEFAULT_DATES_RANGE_OPTIONS,
} from "../../Utils/constants";

// Statuses that should show expiry column and auto-refresh
const EXPIRY_ENABLED_STATUSES = ["pending", "scheduled", "expired"]; // Add "expired" here when needed
const DEFAULT_PAGE_LIMIT = 10;

const ManagePlanRequests = ({ sensitiveAllowedActions }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: DEFAULT_PAGE_LIMIT,
    search: "",
    startDate: "",
    endDate: "",
    requestStatus: "pending",
    sortOrder: "DESC",
  });
  const [loading, setLoading] = useState(false);
  const [requestedByUsers, setRequestedByUsers] = useState([]);
  const [managePlanModalVisible, setManagePlanModalVisible] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [editAmountOnly, setEditAmountOnly] = useState(false);

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });

  const sensitiveAllowedFunctionSet = React.useMemo(
    () => new Set(sensitiveAllowedActions),
    [sensitiveAllowedActions]
  );

  const [tableKey, setTableKey] = useState(Date.now());

  const fetchRequestedByUsers = async () => {
    try {
      const res = await getManagePlanRequestedUsers();
      if (res?.code === 200) {
        setRequestedByUsers(res.result?.users || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchRequestedByUsers();
  }, []);

  const handleGetManagePlanRequests = async () => {
    setLoading(true);
    let hide;

    try {
      const res = await getManagePlanRequests(filters);
      if (res?.code === 200) {
        setData(res.result.managePlans);
        setTotal(res.result.totalManagePlans);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      if (hide) hide();
      setLoading(false);
    }
  };

  const handleApprovePurchaseManagePlan = async (
    id,
    type,
    savedData,
    approveOnFly = true
  ) => {
    try {
      const res = await approveManagePlan(id, type, savedData, approveOnFly);
      if (res?.code === 200) {
        message.success(
          `Manage plan request ${
            approveOnFly ? "approved" : "updated"
          } successfully`
        );
        handleGetManagePlanRequests();
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
  };

  const handleRejectPurchaseManagePlan = async (id, type, savedData) => {
    try {
      const res = await rejectManagePlan(id, type, savedData);
      if (res?.code === 200) {
        message.success(`Manage plan request rejected successfully`);
        handleGetManagePlanRequests();
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
  };

  // Calculate expiration details
  const getExpirationDetails = (createdAt) => {
    const expirationDate = moment(createdAt).add(EXPIRY_INTERVAL, "days");
    const now = moment();

    // Check if expired
    if (now.isAfter(expirationDate)) {
      return { expired: true, display: "Expired", color: "red" };
    }

    // Calculate time remaining
    const hoursRemaining = expirationDate.diff(now, "hours", true);

    // Less than 24 hours - show hours and minutes
    if (hoursRemaining < 24) {
      const duration = moment.duration(expirationDate.diff(now));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      return {
        expired: false,
        display: `${hours ? `${hours}hr ` : ""}${minutes}min`,
        color: "orange",
      };
    }

    // 24+ hours - show days (rounded up)
    const daysRemaining = Math.ceil(hoursRemaining / 24);

    return {
      expired: false,
      display: `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""}`,
      color: daysRemaining < 3 ? "orange" : "green",
    };
  };

  // Build columns array dynamically based on current status
  const columns = React.useMemo(
    () =>
      [
        {
          title: "Date",
          dataIndex: "createdAt",
          key: "createdAt",
          width: 200,
          render: (value, record) => {
            return (
              <Space direction="vertical" size={6}>
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Created At
                  </Typography.Text>
                  <br />
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {moment(value).format("DD MMM YYYY, hh:mm A")}
                  </Typography.Text>
                </div>

                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    By
                  </Typography.Text>
                  <br />
                  <Tag
                    color="blue"
                    style={{ margin: 0, fontSize: 11, fontWeight: 500 }}
                  >
                    {record?.requestCreatedBy?.name || "—"}
                  </Tag>
                </div>

                {record?.scheduledAt ? (
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Scheduled At
                    </Typography.Text>
                    <br />
                    <Typography.Text style={{ fontSize: 12 }}>
                      {moment(record.scheduledAt).format(
                        "DD MMM YYYY, hh:mm A"
                      )}
                    </Typography.Text>
                  </div>
                ) : null}

                {record?.planActivationDate ? (
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Plan Activation Date
                    </Typography.Text>
                    <br />
                    <Typography.Text style={{ fontSize: 12 }}>
                      {moment(record.planActivationDate).format(
                        "DD MMM YYYY, hh:mm A"
                      )}
                    </Typography.Text>
                  </div>
                ) : null}
              </Space>
            );
          },
        },

        {
          title: "Creator",
          dataIndex: "userId",
          key: "userId",
          render: (_, record) => (
            <Space size="small" direction="vertical">
              <Typography>{record?.user?.name}</Typography>
              <Typography>{record?.user?.email}</Typography>
              <Typography>{record?.user?.phone}</Typography>
              <Typography>({record?.user?.host})</Typography>
            </Space>
          ),
        },
        {
          title: "Previous Plan",
          dataIndex: "previousPlan",
          key: "previousPlan",
          render: (_, record) => (
            <Col style={{ textTransform: "capitalize" }}>
              {record?.currentPlan?.platformSetup} -{" "}
              {TIER_NAME_MAP[record?.currentPlan?.tier]}
            </Col>
          ),
        },
        {
          title: "Details",
          dataIndex: "referredBy",
          key: "referredBy",
          render: (_, record) => (
            <Space size="small" direction="vertical">
              <Typography style={{ textTransform: "capitalize" }}>
                <b>Plan Setup:</b> {record?.platformSetup} -{" "}
                {TIER_NAME_MAP[record?.tier]}
              </Typography>

              <Typography style={{ whiteSpace: "nowrap" }}>
                <b>One time amount:</b> {record?.onetimeFee}
              </Typography>
              <Typography>
                <b>Subscription amount:</b> {record?.subscriptionFee} /{" "}
                {record?.paymentPlan}
              </Typography>

              <Typography>
                <b>Total Product Price:</b> {record?.totalProductPrice}
              </Typography>
              {record?.referredBy ? (
                <Space size="small" direction="vertical">
                  <Typography>
                    <b>Referred By:</b>
                  </Typography>
                  <Typography style={{}}>{record?.referredBy?.name}</Typography>
                  <Typography>{record?.referredBy?.email}</Typography>
                  <Typography>{record?.referredBy?.phone}</Typography>
                  <Typography>({record?.referredBy?.host})</Typography>
                </Space>
              ) : (
                "--"
              )}
            </Space>
          ),
        },

        {
          title: "Type",
          dataIndex: "type",
          key: "type",
          render: (_, record) => <Col>{record?.type}</Col>,
        },

        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          render: (_, record) => (
            <Col>
              <Typography style={{ textTransform: "capitalize" }}>
                {record?.requestStatus}
              </Typography>
              {(record?.requestStatus === "approved" ||
                record?.requestStatus === "rejected") &&
              record?.requestStatusUpdatedAt &&
              record?.requestStatusUpdatedBy?.name ? (
                <Typography>
                  on{" "}
                  {moment(record?.requestStatusUpdatedAt).format(
                    "DD MMM YYYY, hh:mm A"
                  )}{" "}
                  by {record?.requestStatusUpdatedBy?.name}
                </Typography>
              ) : null}
            </Col>
          ),
        },

        EXPIRY_ENABLED_STATUSES.includes(filters.requestStatus)
          ? {
              title: "Expires In",
              dataIndex: "expiresIn",
              key: "expiresIn",
              sorter: true,
              sortDirections: ["descend", "ascend", "descend"],
              defaultSortOrder: "descend",
              sortOrder:
                filters.sortOrder === "DESC"
                  ? "descend"
                  : filters.sortOrder === "ASC"
                  ? "ascend"
                  : "descend",
              showSorterTooltip: true,
              render: (_, record) => {
                const expirationDetails = getExpirationDetails(
                  record?.createdAt
                );

                return (
                  <Tag
                    color={expirationDetails.color}
                    style={{ textTransform: "none" }}
                  >
                    {expirationDetails.display}
                  </Tag>
                );
              },
            }
          : null,

        {
          title: "Actions",
          key: "actions",
          render: (_, record) =>
            record?.requestStatus === "pending" ? (
              <Space>
                {sensitiveAllowedFunctionSet.has("managePlan") ? (
                  <Space size="small" direction="vertical">
                    <Button
                      className="siteBtn accentBtnV1"
                      onClick={() => {
                        setFormValues({
                          userId: record?.user?._id,
                          managePlanId: record?._id,
                        });
                        setEditAmountOnly(false);
                        setManagePlanModalVisible(true);
                      }}
                      block
                    >
                      View & Approve
                    </Button>

                    {getExpirationDetails(record?.createdAt).expired ? null : (
                      <Button
                        className="siteBtn accentBtnV1"
                        onClick={() => {
                          setFormValues({
                            userId: record?.user?._id,
                            managePlanId: record?._id,
                          });
                          setEditAmountOnly(true);
                          setManagePlanModalVisible(true);
                        }}
                        block
                      >
                        Edit Amount
                      </Button>
                    )}

                    <Button
                      onClick={() => {
                        handleRejectPurchaseManagePlan(
                          record?._id,
                          "purchase",
                          record
                        );
                      }}
                      block
                      style={{
                        backgroundColor: "#FF4D4F",
                        color: "#fff",
                      }}
                    >
                      Reject
                    </Button>
                  </Space>
                ) : (
                  <Typography>
                    <b>N/A</b>
                  </Typography>
                )}
              </Space>
            ) : null,
        },
      ].filter(Boolean),
    [filters.requestStatus, filters.sortOrder, sensitiveAllowedFunctionSet]
  );

  useEffect(() => {
    // console.log("Filters changed:", filters);
    handleGetManagePlanRequests(filters);
  }, [filters]);

  useEffect(() => {
    if (debouncedSearch) {
      setFilters((prev) => ({
        ...prev,
        search: debouncedSearch,
        page: 1,
      }));
    }
  }, [debouncedSearch]);

  return (
    <>
      <Row gutter={6} justify="space-between" align="middle">
        <Col>
          <Input
            minLength={2}
            className="searchbar"
            value={search}
            style={{ width: 400 }}
            placeholder="Search by partner name, email or mobile"
            onChange={(event) => {
              if (event.target.value.length === 0) {
                setFilters({
                  ...filters,
                  search: "",
                });
              }
              setSearch(event.target.value);
            }}
          />
        </Col>
        <Col>
          <Space>
            <DatePicker.RangePicker
              ranges={DEFAULT_DATES_RANGE_OPTIONS}
              disabledDate={(current) =>
                current && current > moment().endOf("day")
              }
              value={
                filters.startDate && filters.endDate
                  ? [moment(filters.startDate), moment(filters.endDate)]
                  : null
              }
              onChange={(dates) => {
                if (dates && dates.length === 2) {
                  setFilters({
                    ...filters,
                    startDate: dates[0].startOf("day").toISOString(),
                    endDate: dates[1].endOf("day").toISOString(),
                    page: 1,
                  });
                } else {
                  setFilters({
                    ...filters,
                    startDate: "",
                    endDate: "",
                    page: 1,
                  });
                }
              }}
            />
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Requested By"
              style={{ width: 300 }}
              maxTagCount={2}
              maxTagTextLength={15}
              popupMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 350 }}
              value={filters.requestedBy || []}
              onChange={(values) => {
                setFilters({
                  ...filters,
                  requestedBy: values.length ? values : "",
                  page: 1,
                });
              }}
              options={requestedByUsers.map((user) => ({
                value: user._id,
                label: `${user.name} (${user.email})`,
              }))}
            />
          </Space>
        </Col>
      </Row>
      <Row style={{ marginTop: "20px" }}>
        <Col span={12}>
          <Space>
            <Button
              type={filters.requestStatus === "pending" ? "primary" : "default"}
              onClick={() =>
                setFilters({
                  ...filters,
                  requestStatus: "pending",
                  sortOrder: "DESC",
                  page: 1,
                })
              }
            >
              Pending
            </Button>
            <Button
              type={
                filters.requestStatus === "scheduled" ? "primary" : "default"
              }
              onClick={() =>
                setFilters({
                  ...filters,
                  requestStatus: "scheduled",
                  sortOrder: "DESC",
                  page: 1,
                })
              }
            >
              Scheduled
            </Button>
            <Button
              type={filters.requestStatus === "expired" ? "primary" : "default"}
              onClick={() =>
                setFilters({
                  ...filters,
                  requestStatus: "expired",
                  sortOrder: "DESC",
                  page: 1,
                })
              }
            >
              Expired
            </Button>
            <Button
              type={
                filters.requestStatus === "approved" ? "primary" : "default"
              }
              onClick={() =>
                setFilters({
                  ...filters,
                  requestStatus: "approved",
                  page: 1,
                })
              }
            >
              Approved
            </Button>
            <Button
              type={
                filters.requestStatus === "rejected" ? "primary" : "default"
              }
              onClick={() =>
                setFilters({
                  ...filters,
                  requestStatus: "rejected",
                  page: 1,
                })
              }
            >
              Rejected
            </Button>
          </Space>
        </Col>
      </Row>

      <ManagePlanModal
        visible={managePlanModalVisible}
        onClose={() => {
          setFormValues({});
          setEditAmountOnly(false);
          setManagePlanModalVisible(false);
        }}
        onSave={(savedData, type) => {
          if (type === "purchase") {
            handleApprovePurchaseManagePlan(
              formValues?.managePlanId,
              type,
              savedData,
              !editAmountOnly // approveOnlyFly = false when editing amount only
            );
          } else {
            handleRejectPurchaseManagePlan(
              formValues?.managePlanId,
              type,
              savedData,
              !editAmountOnly // approveOnlyFly = false when editing amount only
            );
          }
          handleGetManagePlanRequests();
          setEditAmountOnly(false);
          setManagePlanModalVisible(false);
        }}
        mode="maker-checker"
        userId={formValues?.userId}
        managePlanId={formValues?.managePlanId}
        editAmountOnly={editAmountOnly}
      />

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        style={{
          marginTop: "20px",
        }}
        pagination={{
          current: filters.page,
          total,
          pageSize: filters.limit,
          onChange: (p) => setFilters({ ...filters, page: p }),
        }}
        onChange={(pagination, tableFilters, sorter) => {
          console.log("Table onChange triggered:", {
            pagination,
            tableFilters,
            sorter,
          });

          let newSortOrder;

          // Handle sorting - check both columnKey and field
          if (
            sorter &&
            (sorter.columnKey === "expiresIn" || sorter.field === "expiresIn")
          ) {
            if (sorter.order === "descend") {
              newSortOrder = "DESC";
            } else if (sorter.order === "ascend") {
              newSortOrder = "ASC";
            } else if (sorter.order === undefined || sorter.order === null) {
              // If order is undefined/null, toggle based on current state
              newSortOrder = filters.sortOrder === "DESC" ? "ASC" : "DESC";
            } else {
              // Default fallback
              newSortOrder = "DESC";
            }
          }

          // setting the filters for the table
          setFilters((prevFilters) => {
            const newFilters = {
              ...prevFilters,
              ...(newSortOrder && { sortOrder: newSortOrder }),
              limit: pagination.pageSize ?? DEFAULT_PAGE_LIMIT,
            };

            // if the sort order changes, reset to page 1
            if (prevFilters.sortOrder !== newFilters.sortOrder) {
              newFilters.page = 1;
            } else {
              // if the sort order doesn't change, increment the page
              newFilters.page = prevFilters.page;
            }

            return newFilters;
          });
        }}
        rowKey={(record) => record._id}
      />
    </>
  );
};

ManagePlanRequests.propTypes = {
  sensitiveAllowedActions: PropTypes.shape([]),
};

export default ManagePlanRequests;
